/**
 * One field for scanning and searching (T07-23, design/05 §3.1):
 *
 * - a barcode / QR scanner in keyboard mode (HID "wedge") types the code very fast and ends with
 *   Enter — a **burst** (every key < `burstMs` after the previous one, ≥ `minLength` characters)
 *   followed by Enter is a scan, even when the field does not have the focus (the cashier just
 *   tapped something else) → `onScan(code)`;
 * - Enter after typing by hand also adds the code (a cert number typed from the slab);
 * - typing without Enter searches by name after `debounceMs` (150 ms) → `onSearch(q)`.
 *
 * `ScanDetector` is the pure part (unit-tested); prefixes/suffixes of odd scanner models are set
 * in settings (T07-34).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";

export interface ScanDetectorOptions {
  burstMs?: number;
  minLength?: number;
  /** Characters a scanner adds before / after the code (removed). */
  prefix?: string;
  suffix?: string;
}

export class ScanDetector {
  private buffer = "";
  private last = 0;
  private burst = true;
  private readonly burstMs: number;
  private readonly minLength: number;
  private readonly prefix: string;
  private readonly suffix: string;

  constructor(options: ScanDetectorOptions = {}) {
    this.burstMs = options.burstMs ?? 35;
    this.minLength = options.minLength ?? 4;
    this.prefix = options.prefix ?? "";
    this.suffix = options.suffix ?? "";
  }

  /** Feed one key (`KeyboardEvent.key`) at time `at` (ms) → the scanned code on Enter, else null. */
  feed(key: string, at: number): string | null {
    if (key === "Enter" || key === "Tab") {
      const code = this.clean(this.buffer);
      const scanned = this.burst && code.length >= this.minLength ? code : null;
      this.reset();
      return scanned;
    }
    if (key.length !== 1) {
      return null; // Shift, arrows …
    }
    if (this.buffer && at - this.last > this.burstMs) {
      // a slow key: a person typing (start a new candidate from this key)
      this.buffer = "";
      this.burst = true;
    }
    this.buffer += key;
    this.last = at;
    return null;
  }

  reset(): void {
    this.buffer = "";
    this.burst = true;
    this.last = 0;
  }

  clean(code: string): string {
    let out = code.trim();
    if (this.prefix && out.startsWith(this.prefix)) {
      out = out.slice(this.prefix.length);
    }
    if (this.suffix && out.endsWith(this.suffix)) {
      out = out.slice(0, -this.suffix.length);
    }
    return out.trim();
  }
}

export interface UseScanInputOptions extends ScanDetectorOptions {
  /** The key the scanner ends a code with (設定 → スキャナー; Tab for scanners set that way). */
  terminator?: "Enter" | "Tab";
  onScan: (code: string) => void;
  onSearch: (query: string) => void;
  debounceMs?: number;
  /** Listen for scanner bursts anywhere on the page (default true). */
  global?: boolean;
  enabled?: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export function useScanInput(options: UseScanInputOptions) {
  const {
    onScan,
    onSearch,
    debounceMs = 150,
    global = true,
    enabled = true,
    terminator = "Enter",
  } = options;
  const [value, setValue] = useState("");
  const handlers = useRef({ onScan, onSearch });
  useEffect(() => {
    handlers.current = { onScan, onSearch };
  });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { burstMs = 35, minLength = 4, prefix = "", suffix = "" } = options;
  // the scanner's prefix comes from 設定 (read asynchronously) — a new detector when it changes
  const detector = useMemo(
    () => new ScanDetector({ burstMs, minLength, prefix, suffix }),
    [burstMs, minLength, prefix, suffix],
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  const cancelSearch = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const submit = useCallback(
    (raw: string) => {
      cancelSearch();
      const code = detector.clean(raw);
      setValue("");
      handlers.current.onSearch("");
      if (code) {
        handlers.current.onScan(code);
      }
    },
    [detector],
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setValue(next);
    cancelSearch();
    timer.current = setTimeout(() => handlers.current.onSearch(next.trim()), debounceMs);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "Enter" ||
      (terminator === "Tab" && event.key === "Tab" && event.currentTarget.value)
    ) {
      event.preventDefault();
      submit(event.currentTarget.value);
    } else if (event.key === "Escape") {
      cancelSearch();
      setValue("");
      handlers.current.onSearch("");
    }
  };

  // a scanner burst while the focus is elsewhere (not in another field)
  useEffect(() => {
    if (!global || !enabled) {
      return;
    }
    const detect = detector;
    const listen = (event: globalThis.KeyboardEvent) => {
      if (
        event.target === inputRef.current ||
        isTypingTarget(event.target) ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return;
      }
      const code = detect.feed(event.key, event.timeStamp || performance.now());
      if (code) {
        event.preventDefault();
        handlers.current.onScan(code);
      }
    };
    window.addEventListener("keydown", listen, true);
    return () => window.removeEventListener("keydown", listen, true);
  }, [global, enabled, detector]);

  useEffect(() => cancelSearch, []);

  return {
    value,
    setValue,
    inputRef,
    inputProps: { value, onChange, onKeyDown, ref: inputRef },
    focus: () => inputRef.current?.focus(),
  };
}
